import 'reflect-metadata'
import { inject, injectable } from 'inversify'
import type { FetchError, FetchOptions, FetchResponse } from 'ofetch'

import { AbortRequestController } from '../AbortRequestController'
import { ApiClientStrategy } from '../Clients/ApiClientStrategy'
import type { ApiClientResponse } from '../Clients/types'
import {
  type MethodCfg,
  resolveMethodMeta,
} from '../GateWays/ApiGateWayDecorators'
import { RequestBuilder, methodsHasPayload } from '../RequestBuilder'
import { RequestTracker, type RequestTrackInfo } from '../RequestTracker'

import { makePrefixTag } from '../../../common/Utils'

export type GatewayFetchError = FetchError & {
  response: FetchError['response'] & { data: unknown }
}

const transferHeaders = Object.freeze(['Uuid', 'Session-ID'])

@injectable()
export abstract class ApiGateWay {
  constructor(
    @inject(RequestTracker)
    protected readonly requestTracker: RequestTracker,
    @inject(RequestBuilder)
    protected readonly requestBuilder: RequestBuilder,
    @inject(ApiClientStrategy)
    protected readonly apiClientStrategy: ApiClientStrategy,
    @inject(AbortRequestController)
    protected readonly abortRequestController: AbortRequestController,
  ) {}

  static PREFIX: string = ''

  /**
   * Отправка запроса через выбранный ApiClient
   *
   * Параметры, метод и url запроса берутся из декораторов полей
   */
  protected async send<T = void>(payload?: unknown): Promise<T> {
    return this.sendRaw<T>(payload)
      .then((response: FetchResponse<T>) => this.onResponseHandler<T>(response))
      .catch((error: GatewayFetchError) =>
        this.onErrorHandler(error),
      ) as Promise<T>
  }

  protected async sendRaw<T = void>(
    payload?: unknown,
  ): Promise<FetchResponse<T>> {
    // Данные, необходимые для отправки запроса
    const cfg: MethodCfg = resolveMethodMeta(this)
    const rawOptions
      = typeof cfg.options === 'function' ? cfg.options() : cfg.options || {}
    const { body: normalizedPayload, options }
      = await this.requestBuilder.prepareRequest(cfg.type, payload, rawOptions)
    const url = this.prefix`${cfg.path}`

    // ApiClient через который будет отправляться запрос
    const client = this.client(options, normalizedPayload)

    // Отправка запроса через ApiClient
    const { response, abort } = Reflect.apply(
      client[cfg.type],
      client,
      methodsHasPayload(cfg.type)
        ? [url, normalizedPayload, options]
        : [url, options],
    ) as ApiClientResponse<T>

    this.trackRequest({
      methodKey: cfg.methodKey,
      id: Symbol(cfg.methodKey),
      abort,
    })

    return response
  }

  /**
   * Сохранение отправляемых запросов в специальный класс-трекер
   */
  private trackRequest(requestInfo: RequestTrackInfo) {
    this.requestTracker.addRequest(requestInfo)
    this.requestTracker.setCurrentRequest(requestInfo)
    this.abortRequestController.abortSameMethods(requestInfo) // Отмена повторяющихся запросов
  }

  /**
   * Получение ApiClien'а исходя из условий стратегии
   */
  private client(options: FetchOptions<'json' | 'stream'>, body?: unknown) {
    return this.apiClientStrategy.getClient(options, body)
  }

  /**
   * Хендлер на получение успешного ответа от сервера
   */
  private onResponseHandler<T>(response?: FetchResponse<T>): Promise<T> {
    const headers: Record<string, string> = response?.headers
      ? Object.fromEntries(Object.entries(response?.headers))
      : {}

    for (const i in transferHeaders) {
      const key = transferHeaders?.[i]
      if (!key) {
        continue
      }
      const headerValue = headers[key]?.toLocaleLowerCase()
      /**
       * TODO: спорный момент, что заголовки только на клиенте трансферятся,
       * это может на SEO влиять в результате, раз контекст запроса в SSR и на клиенте отличается
       */
      if (import.meta.client && headerValue) {
        window.localStorage.setItem(key, headerValue)
      }
    }

    return response?._data as Promise<T>
  }

  /**
   * Хендлер ошибки
   */
  private onErrorHandler(error: GatewayFetchError) {
    if (!error?.response?.data && error?.response?._data) {
      error.response.data = error.response._data
    }

    throw error
  }

  protected get prefix() {
    return makePrefixTag((<typeof ApiGateWay> this.constructor).PREFIX)
  }
}
